/**
 * Globale Suche über den Bestand.
 *
 * Die Palette (Strg+K) durchsuchte bis zum 25.08.2026 **ausschließlich die 19
 * Aufgabenkacheln** aus `entwurf/katalog.ts` — nach Titel und Beschreibung,
 * ohne einen einzigen Aufruf an die Schnittstelle. Wer `APM-AM-00` eingab,
 * bekam „Keine Ergebnisse", und der Leertext darunter riet ausgerechnet zu
 * „einem Rollenschlüssel, einem Anwendungsnamen oder einer Group ID" — also
 * zu genau dem, was sie nicht finden konnte. Der Kunde hat es am 24.08.2026
 * gemeldet: „Suchindex funktioniert überhaupt nicht."
 *
 * Serverseitig war alles vorhanden: `abfrage.ts` sucht seit dem 06.10.2026
 * ohne weitere Angabe als Teilstring (vorher nur ab Wortanfang, und der
 * Enthaelt-Modus wurde von hier nie angefordert), und `sammlungen.ts` gibt
 * 28 Felder als durchsuchbar frei.
 *
 * ## Vier Sammlungen, fünf Treffer je Sammlung
 *
 * Rollen, Assets, technische Gruppen und Zuordnungen. Mehr wäre kein besseres
 * Ergebnis, sondern eine zweite Trefferliste in einem Fenster, das über die
 * Tastatur bedient wird; wer mehr braucht, landet über den Treffer auf der
 * Liste, die blättern kann.
 *
 * ## Nur, was gelesen werden darf
 *
 * Ohne Leserecht wird die Sammlung **gar nicht** abgefragt. Sonst liefe die
 * Suche in eine Reihe 403 und sähe kaputt aus, obwohl sie richtig arbeitet.
 * Geprüft wird gegen die Rechte aus `/api/ich`, nicht gegen die im Browser
 * umschaltbare Persona.
 */

import { useListe } from './haken';

export interface Suchtreffer {
  /** Objektart als Beschriftung — „Rolle", „Applikation", „Technische Gruppe". */
  art: string;
  titel: string;
  unter: string;
  pfad: string;
  farbe: string;
}

interface Rollenzeile { rolleId: string; name: string; oeCode: string }
interface Assetzeile { assetId: string; name: string; assetTyp: string; kategorie: string }
interface Gruppenzeile { gruppenId: string; name: string; groupId: string }
interface Zuordnungszeile {
  berechtigungId: string; rolleId: string; asset: string;
  assetTyp: string; berechtigungsart: string;
}

/** Route der Assetliste je Objektart — sie tragen sechs Arten auf fünf Seiten. */
const ASSETROUTE: Record<string, string> = {
  Applikation: '/assets/applikationen',
  Fileserver: '/assets/fileserver',
  Postfach: '/assets/exchange',
  Verteiler: '/assets/exchange',
  M365: '/assets/m365',
  Hardware: '/assets/hardware',
  MobileApp: '/assets/applikationen',
};

const ASSETFARBE: Record<string, string> = {
  Applikation: '--art-app',
  Fileserver: '--art-file',
  Postfach: '--art-mail',
  Verteiler: '--art-mail',
  M365: '--art-m365',
  Hardware: '--art-hw',
  MobileApp: '--art-mobile',
};

export interface Suchergebnis {
  treffer: Suchtreffer[];
  laedt: boolean;
  /** Mindestens eine Sammlung hat nicht geantwortet. */
  fehler: boolean;
  /** Zu wenig Zeichen, oder kein Leserecht auf irgendeine Sammlung. */
  aus: boolean;
}

/**
 * @param begriff bereits entprellt — dieser Haken entprellt nicht selbst.
 */
export function useGlobaleSuche(
  begriff: string,
  mandant: string,
  darfDas: (recht: string | null) => boolean,
): Suchergebnis {
  /* Zwei Zeichen sind die Untergrenze. Bei einem einzigen trifft jede
     Zeichenkette, und der Server rechnet über den ganzen Bestand für ein
     Ergebnis, das niemandem hilft. */
  const genug = begriff.trim().length >= 2;
  const q = begriff.trim();

  const darfRollen = darfDas('rollen.lesen');
  const darfAssets = darfDas('assets.lesen');
  const darfGruppen = darfDas('gruppen.lesen');
  const darfMatrix = darfDas('matrix.lesen');

  const rollen = useListe<Rollenzeile>('rollen', mandant, {
    seite: 1, groesse: 5, sortierung: 'rolleId', richtung: 'auf',
    suche: q, aus: !genug || !darfRollen,
  });
  const assets = useListe<Assetzeile>('assets', mandant, {
    seite: 1, groesse: 5, sortierung: 'name', richtung: 'auf',
    suche: q, aus: !genug || !darfAssets,
  });
  const gruppen = useListe<Gruppenzeile>('gruppen', mandant, {
    seite: 1, groesse: 5, sortierung: 'name', richtung: 'auf',
    suche: q, aus: !genug || !darfGruppen,
  });
  const zuordnungen = useListe<Zuordnungszeile>('zuordnungen', mandant, {
    seite: 1, groesse: 5, sortierung: 'asset', richtung: 'auf',
    suche: q, aus: !genug || !darfMatrix,
  });

  const treffer: Suchtreffer[] = [];

  for (const r of rollen.daten?.zeilen ?? []) {
    treffer.push({
      art: 'Rolle', titel: r.rolleId + ' · ' + r.name,
      unter: 'Organisationseinheit ' + r.oeCode,
      pfad: '/rollen/' + encodeURIComponent(r.rolleId),
      farbe: '--art-rolle',
    });
  }
  for (const a of assets.daten?.zeilen ?? []) {
    const route = ASSETROUTE[a.assetTyp] ?? '/assets/applikationen';
    treffer.push({
      art: a.assetTyp, titel: a.name,
      unter: a.kategorie || a.assetTyp,
      /* Assets haben keine eigene Detailroute — sie öffnen als Panel in ihrer
         Liste. Der Treffer führt deshalb auf die Liste, vorgefiltert auf den
         Namen; `Datentabelle` liest `?suche=` und übernimmt ihn. */
      pfad: route + '?suche=' + encodeURIComponent(a.name),
      farbe: ASSETFARBE[a.assetTyp] ?? '--art-app',
    });
  }
  for (const g of gruppen.daten?.zeilen ?? []) {
    treffer.push({
      art: 'Technische Gruppe', titel: g.name,
      unter: g.groupId || g.gruppenId,
      pfad: '/assets/gruppen?suche=' + encodeURIComponent(g.name),
      farbe: '--art-gruppe',
    });
  }
  for (const z of zuordnungen.daten?.zeilen ?? []) {
    treffer.push({
      art: 'Berechtigung', titel: z.asset + ' · ' + z.berechtigungsart,
      unter: 'in Rolle ' + z.rolleId,
      pfad: '/rollen/' + encodeURIComponent(z.rolleId),
      farbe: '--art-rolle',
    });
  }

  const abrufe = [
    darfRollen ? rollen : null,
    darfAssets ? assets : null,
    darfGruppen ? gruppen : null,
    darfMatrix ? zuordnungen : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);

  return {
    treffer,
    laedt: genug && abrufe.some((a) => a.laedt),
    fehler: genug && abrufe.some((a) => !!a.fehler),
    aus: !genug || abrufe.length === 0,
  };
}
